import type { DiscoverField, DiscoverProposal } from "@/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useState } from "react";

type Picks = Record<string, Set<DiscoverField>>;

/** New rules and plain upstream changes start ticked; hand-set titles do not. */
function defaultPicks(items: DiscoverProposal[]): Picks {
  const picks: Picks = {};
  for (const item of items) {
    const fields = new Set<DiscoverField>();
    if (item.status === "new" || (item.title.changed && !item.title_manual)) fields.add("title");
    if (item.pattern.changed) fields.add("pattern");
    picks[item.slug] = fields;
  }
  return picks;
}

function Change({
  label,
  value,
  checked,
  onToggle,
  warn,
  mono,
}: {
  label: string;
  value: DiscoverProposal["title"];
  checked: boolean;
  onToggle: () => void;
  warn?: string;
  mono?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-xs">
      <input type="checkbox" className="mt-0.5 accent-[var(--color-accent)]" checked={checked} onChange={onToggle} />
      <span className="w-14 shrink-0 text-muted">{label}</span>
      <span className={mono ? "min-w-0 break-all font-mono" : "min-w-0"}>
        <span className="text-muted line-through">{value.current ?? "—"}</span>
        <span className="px-1.5 text-muted">→</span>
        <span className="text-ink">{value.proposed ?? "—"}</span>
        {warn ? <span className="ml-2 text-amber">{warn}</span> : null}
      </span>
    </label>
  );
}

/**
 * Comparison of the source's rules with what the upstream catalog suggests;
 * only the ticked rows and fields are taken over.
 */
export function DiscoverReview({
  items,
  found,
  applying,
  onApply,
  onCancel,
}: {
  items: DiscoverProposal[];
  found: number;
  applying: boolean;
  onApply: (picks: { slug: string; fields: DiscoverField[] }[]) => void;
  onCancel: () => void;
}) {
  const [picks, setPicks] = useState<Picks>(() => defaultPicks(items));

  function toggle(slug: string, field: DiscoverField) {
    setPicks((prev) => {
      const fields = new Set(prev[slug]);
      if (fields.has(field)) fields.delete(field);
      else fields.add(field);
      return { ...prev, [slug]: fields };
    });
  }

  function toggleNew(slug: string) {
    setPicks((prev) => ({
      ...prev,
      [slug]: prev[slug]?.size ? new Set() : new Set<DiscoverField>(["title", "pattern"]),
    }));
  }

  const selected = Object.entries(picks)
    .filter(([, fields]) => fields.size > 0)
    .map(([slug, fields]) => ({ slug, fields: [...fields] }));
  const newItems = items.filter((item) => item.status === "new");
  const changed = items.filter((item) => item.status === "changed");

  if (items.length === 0) {
    return (
      <div className="mb-4 rounded-lg border border-hairline bg-surface p-3 text-sm">
        <p>
          Alles aktuell: {found} {found === 1 ? "Gruppe" : "Gruppen"} in der Quelle, keine Abweichung zu den Regeln.
        </p>
        <div className="mt-2">
          <Button size="sm" onClick={onCancel}>
            Schließen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <section className="mb-4 rounded-lg border border-hairline bg-surface" aria-label="Vorschläge aus der Quelle">
      <div className="border-b border-hairline px-3 py-2">
        <h2 className="text-sm font-semibold">Vorschläge aus der Quelle</h2>
        <p className="text-[11px] text-muted">
          Nur angehakte Punkte werden übernommen. Von Hand geänderte Titel sind nicht vorausgewählt.
        </p>
      </div>

      {newItems.length ? (
        <div className="border-b border-hairline px-3 py-2">
          <p className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted">
            Neu ({newItems.length})
          </p>
          <ul className="space-y-1">
            {newItems.map((item) => (
              <li key={item.slug}>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="accent-[var(--color-accent)]"
                    checked={(picks[item.slug]?.size ?? 0) > 0}
                    onChange={() => toggleNew(item.slug)}
                  />
                  <span>{item.title.proposed}</span>
                  <span className="font-mono text-[11px] text-muted">{item.slug}</span>
                  <span className="truncate font-mono text-[11px] text-muted">/{item.pattern.proposed}/</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {changed.length ? (
        <div className="border-b border-hairline px-3 py-2">
          <p className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted">
            Geändert ({changed.length})
          </p>
          <ul className="space-y-2">
            {changed.map((item) => (
              <li key={item.slug} className="space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{item.title.current}</span>
                  <span className="font-mono text-[11px] text-muted">{item.slug}</span>
                  {item.title_manual ? <Badge tone="muted">von Hand benannt</Badge> : null}
                </div>
                <div className="space-y-1 pl-1">
                  {item.title.changed ? (
                    <Change
                      label="Titel"
                      value={item.title}
                      checked={picks[item.slug]?.has("title") ?? false}
                      onToggle={() => toggle(item.slug, "title")}
                      warn={item.title_manual ? "überschreibt deinen Titel" : undefined}
                    />
                  ) : null}
                  {item.pattern.changed ? (
                    <Change
                      label="Muster"
                      value={item.pattern}
                      checked={picks[item.slug]?.has("pattern") ?? false}
                      onToggle={() => toggle(item.slug, "pattern")}
                      mono
                    />
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <Button
          variant="primary"
          size="sm"
          disabled={applying || selected.length === 0}
          onClick={() => onApply(selected)}
        >
          {applying ? "Übernehme…" : `${selected.length} ${selected.length === 1 ? "Regel" : "Regeln"} übernehmen`}
        </Button>
        <Button size="sm" variant="ghost" disabled={applying} onClick={onCancel}>
          Abbrechen
        </Button>
      </div>
    </section>
  );
}
